import { useRef } from "react";

/**
 * Spotlight wrapper — a subtle brass cursor-following glow.
 * Attach as a relative-positioned wrapper around any card.
 * The glow only activates while the mouse is over the element (perf-friendly).
 *
 * Props:
 *  - className: forwarded to the wrapper
 *  - tone: "light" | "dark"  — sets the glow opacity/intensity
 *  - radius: pixel size of the glow halo (default 320)
 */
export default function Spotlight({
  children,
  className = "",
  tone = "dark",
  radius = 320,
  as: Tag = "div",
}) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
    el.style.setProperty("--alpha", "1");
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--alpha", "0");
  };

  // Brass HSL: 34 39% 63%
  // Light tone (on cream backgrounds): lower opacity so it doesn't wash out
  const baseOpacity = tone === "light" ? 0.18 : 0.28;

  const bg =
    tone === "light"
      ? `radial-gradient(${radius}px ${radius}px at var(--mx, -100px) var(--my, -100px), hsla(34, 60%, 55%, calc(${baseOpacity} * var(--alpha, 0))), transparent 70%)`
      : `radial-gradient(${radius}px ${radius}px at var(--mx, -100px) var(--my, -100px), hsla(34, 70%, 65%, calc(${baseOpacity} * var(--alpha, 0))), transparent 70%)`;

  return (
    <Tag
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className={`relative ${className}`}
      style={{ "--alpha": 0 }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{ background: bg }}
      />
      {children}
    </Tag>
  );
}
